/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { ReactNode, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { UseQueryResult } from '@tanstack/react-query'
import { Alert, Button, Center, Loader } from '@mantine/core'

export type LoadingHelperProps = {
  always?: boolean
  errorFunc?: (error: Error) => ReactNode
  query: UseQueryResult
  placeholder?: ReactNode
  render: (data: unknown) => ReactNode
}

function LoadingHelper (props: LoadingHelperProps) {
  const {
    always,
    errorFunc,
    query,
    placeholder,
    render
  } = props

  const {
    data,
    error,
    refetch,
    isFetching
  } = query

  const showPlaceholder = query.isInitialLoading || (query.isFetching && always)

  const content = useMemo(() => (
    (!showPlaceholder && data != null) ? render(data) : null
  ), [data, render, showPlaceholder])

  const errorContent = useMemo(() => (
    (!isFetching && error && errorFunc && error instanceof Error) ? errorFunc(error) : null
  ), [error, errorFunc, isFetching])

  if (showPlaceholder) {
    return placeholder || (
      <Center>
        <Loader />
      </Center>
    )
  }

  if (error instanceof Error) {
    return errorContent || (
      <Alert
        mt="md"
        mx="auto"
        color="red"
        title={
          <FormattedMessage
            id="loading_error"
            defaultMessage="Erreur de chargement"
          />}
      >
        {error.message}
        <br />
        <Button
          mt="md"
          color="red"
          onClick={() => {
            refetch()
          }}
        >
          <FormattedMessage
            id="retry"
            defaultMessage="Réessayer"
          />
        </Button>
      </Alert>
    )
  }
  return content
}

export default LoadingHelper
